import React, { useCallback } from 'react';
import { useAppSelector } from '../hooks/redux';
import { useDashboardData } from '../hooks/useDashboardData';
import DashboardStats from '../components/dashboard/DashboardStats';
import RecentActivity from '../components/dashboard/RecentActivity';
import QuickActions from '../components/dashboard/QuickActions';
import InventoryAlerts from '../components/dashboard/InventoryAlerts';
import WorkerPerformance from '../components/dashboard/WorkerPerformance';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const Dashboard: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading } = useAppSelector((state) => state.auth);
  const { data, loading, error, refetch } = useDashboardData();
  
  // Extract data for easier use
  const dailySummary = data?.dailySummary;
  const inventoryStatus = data?.inventoryStatus;
  const userStats = data?.userStats;
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Don't render dashboard if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Show loading while authentication is being verified
  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-12">
          <LoadingSpinner size="lg" className="text-blue-600" />
          <p className="mt-4 text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'manager':
        return 'Full system access with administrative privileges';
      case 'executive1':
        return 'Order processing and inventory viewing capabilities';
      case 'executive2':
        return 'Stock management and order verification';
      case 'worker':
        return 'Order picking and task management';
      default:
        return 'Standard user access';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-12">
          <LoadingSpinner size="lg" className="text-blue-600" />
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400" role="img" aria-label="error">❌</span>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                Failed to load dashboard data
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={handleRefresh}
                  className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {getGreeting()}, {user?.full_name || user?.username}!
            </h1>
            <p className="mt-2 text-gray-600">
              {getRoleDescription(user?.role || '')}
            </p>
            <div className="mt-1 text-sm text-gray-500">
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>          <button
            onClick={handleRefresh}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <span className="mr-2 text-gray-500">↻</span>
            Refresh
          </button>
        </div>
      </div>

      {/* Dashboard Statistics */}
      <DashboardStats 
        dailySummary={dailySummary}
        inventoryStatus={inventoryStatus}
        userRole={user?.role || ''}
      />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Quick Actions and Worker Performance */}
        <div className="lg:col-span-1 space-y-6">
          <QuickActions userRole={user?.role || ''} />
          
          {(user?.role === 'manager' || user?.role === 'executive1') && (
            <WorkerPerformance 
              dailySummary={dailySummary}
              userRole={user?.role || ''}
            />
          )}
        </div>

        {/* Center Column - Recent Activity */}
        <div className="lg:col-span-1">
          <RecentActivity 
            dailySummary={dailySummary}
            userRole={user?.role || ''}
          />
        </div>

        {/* Right Column - Inventory Alerts and System Info */}
        <div className="lg:col-span-1 space-y-6">
          {(user?.role === 'manager' || user?.role === 'executive2') && (
            <InventoryAlerts 
              inventoryStatus={inventoryStatus}
              userRole={user?.role || ''}
            />
          )}

          {/* Role Information Card */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Your Account</h3>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Role:</span>
                <p className="text-sm text-gray-900 capitalize">{user?.role}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Username:</span>
                <p className="text-sm text-gray-900">{user?.username}</p>
              </div>
              {user?.email && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Email:</span>
                  <p className="text-sm text-gray-900">{user.email}</p>
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-gray-500">Last Login:</span>
                <p className="text-sm text-gray-900">
                  {user?.last_login_at ? 
                    new Date(user.last_login_at).toLocaleString() : 
                    'Unknown'
                  }
                </p>
              </div>
              {user?.permissions?.length ? (
                <div>
                  <span className="text-sm font-medium text-gray-500 block mb-2">Permissions:</span>
                  <div className="flex flex-wrap gap-1">
                    {user.permissions.slice(0, 3).map((permission, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {permission}
                      </span>
                    ))}
                    {user.permissions.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        +{user.permissions.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* System Status Card */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">System Status</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-center">
                <div className="h-3 w-3 bg-green-400 rounded-full mr-3"></div>
                <span className="text-sm text-gray-900">All systems operational</span>
              </div>
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  Last updated: {new Date().toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Stats for Managers */}
      {user?.role === 'manager' && userStats && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">System Overview</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{userStats.total || 0}</div>
                <div className="text-sm text-gray-500">Total Users</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{userStats.active || 0}</div>
                <div className="text-sm text-gray-500">Active Users</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{inventoryStatus?.total_styles || 0}</div>
                <div className="text-sm text-gray-500">Total Styles</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{dailySummary?.orders?.total || 0}</div>
                <div className="text-sm text-gray-500">Orders Today</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
